import { action, makeObservable, observable, reaction } from 'mobx';
import { parseNumberByPrecision } from '../../../core/helpers/parseNumberByPrecision';
import { getKeyByValue } from '../rouletteCore/helpers/getKeyByValue';
import { marketType } from '../rouletteCore/constants';
import { userInfoStore } from '../../../store/userInfoStore';
import { allChipsTotal } from '../rouletteCore/helpers/chipsTotal';
import { soundService } from '../../../services/baseSoundService/soundService';
import { RouletteSoundsName } from '../rouletteCore/constants/sounds';
import { sumWithDecimal } from '../rouletteCore/helpers/sumWithDecimal';
import { notificationStore } from '../../../store/notificationStore';
import { ValidationType } from '../../../core/constants/validationType';
import { freeBetStore } from '../../../store/freeBetStore';

class RouletteBetsStore {
  isBetsClosed = false;

  result = {
    resultNumber: 0,
    isWin: false,
    odd: null,
    winAmount: null,
  };

  resultNumTimeout = null;

  undoBetCount = [];

  resultNumberForTable = null;

  allBetsModels = [];

  isSecondClick = false;

  lastResults = [];

  constructor() {
    makeObservable(this, {
      lastResults: observable,
      isBetsClosed: observable,
      undoBetCount: observable,
      allBetsModels: observable,
      resultNumberForTable: observable,
      result: observable.deep,
      isSecondClick: observable,
      setDoBet: action.bound,
      setUndoBet: action.bound,
      setDoubleBet: action.bound,
      setLastResults: action.bound,
      removeBetModel: action.bound,
      checkValidChip: action.bound,
      setIsBetsClosed: action.bound,
      setUndoBetCount: action.bound,
      setResultNumberForTable: action.bound,
      setResult: action.bound,
      setIsSecondClick: action.bound,
      updateLastResults: action.bound,
      handleDoBetWithValid: action.bound,
      handleDoubleBetWithValid: action.bound,
      handleChangeAllBetsModels: action.bound,
    });

    reaction(
      () => this.isBetsClosed,
      (value) => {
        const { setIsAllowedChangeCurrency } = userInfoStore;
        setIsAllowedChangeCurrency(!value);
      }
    );
    reaction(
      () => this.isBetsClosed,
      (value) => {
        // const { setIsAllowedChangeCurrency } = userInfoStore;
        // setIsAllowedChangeCurrency(!value);
        if (freeBetStore.currentFreeBet && !freeBetStore.currentFreeBet.remainingCount && !value) {
          freeBetStore.setIsShowFreeBetPopup(true);
        }
      }
    );
    reaction(
      () => freeBetStore.currentFreeBet,
      (value, prevValue) => {
        if (prevValue && !value) {
          const { setSelectedChip, defaultBetAmount, setBetAmount } = userInfoStore;
          const selectedQuickBet = userInfoStore.quickBets.find(
            (item) => item.value === defaultBetAmount
          );
          setSelectedChip(selectedQuickBet?.chip);
          setBetAmount(selectedQuickBet.value);
        }
      }
    );
    reaction(
      () => this.allBetsModels,
      () => {
        userInfoStore.setTotalBetAmount(
          allChipsTotal(this.allBetsModels[0], userInfoStore.precisionForBetModel)
        );
      }
    );
  }

  setLastResults(lastResults) {
    this.lastResults = lastResults.map((item, index) => ({ ...item, id: index }));
  }

  setUndoBetCount(count) {
    this.undoBetCount = [count, ...this.undoBetCount];
  }

  setResult(result) {
    this.result = result;
  }

  setIsBetsClosed(bool) {
    this.isBetsClosed = bool;
  }

  setIsSecondClick(bool) {
    this.isSecondClick = bool;
  }

  updateLastResults(lastResult) {
    const updatedLastResult = [...this.lastResults, lastResult];
    if (updatedLastResult.length > 5) {
      updatedLastResult.splice(0, 1);
    }
    const lastEl = updatedLastResult.pop();
    this.setLastResults(updatedLastResult);
    setTimeout(() => {
      this.setLastResults([...updatedLastResult, lastEl]);
    }, 0);
  }

  createBetModelByNumber(model, isCombination) {
    if (isCombination) {
      return {
        type: marketType[model.type],
        amount: model[model.type],
      };
    }
    return {
      bet: {
        type: marketType[model.type],
        amount: model[model.type],
      },
    };
  }

  createBetModelByCombination(model, type) {
    return {
      bet: {
        type: marketType[type],
      },
      includedBets: model.map((item) => this.createBetModelByNumber(item, true)),
    };
  }

  handleChangeAllBetsModels(isDoubleChips, chipData, betType) {
    let allBets;
    const lastBetModel = this.allBetsModels[0] || [];

    if (isDoubleChips) {
      const betModel = lastBetModel.betModel.map((item) => {
        if (item?.includedBets) {
          return {
            bet: { ...item.bet },
            isDouble: true,
            includedBets: item.includedBets.map((betsItem) => {
              const itemAmount = isDoubleChips.find(
                (bet) =>
                  bet.type === getKeyByValue(marketType, betsItem.type) &&
                  bet.betType === getKeyByValue(marketType, item.bet.type)
              );
              return {
                ...betsItem,
                amount: itemAmount[itemAmount.type],
              };
            }),
          };
        }
        const itemAmount = isDoubleChips.find(
          (bet) => bet.type === getKeyByValue(marketType, item.bet.type) && !bet?.betType
        );
        return { bet: { ...item.bet, amount: itemAmount[itemAmount.type] } };
      });

      return {
        betModel,
      };
    }
    if (!betType) {
      const isTypeInLastBetModel = lastBetModel?.betModel?.some(
        (item) => item.bet.type === marketType[chipData.type]
      );
      if (isTypeInLastBetModel) {
        allBets = {
          betModel: lastBetModel?.betModel.map((item) => {
            if (item.bet.type === marketType[chipData.type]) {
              return this.createBetModelByNumber(chipData);
            }
            return item;
          }),
        };
      } else {
        allBets = {
          betModel: [this.createBetModelByNumber(chipData), ...(lastBetModel?.betModel || [])],
        };
      }
      return allBets;
    }
    const isTypeInLastBetModel = lastBetModel?.betModel?.some(
      (item) => item.bet.type === marketType[betType]
    );
    if (isTypeInLastBetModel) {
      allBets = {
        betModel: lastBetModel?.betModel.map((item) => {
          if (item.bet.type === marketType[betType]) {
            return {
              bet: {
                type: marketType[betType],
              },
              includedBets: item.includedBets.map(({ type }) => {
                const itemAmount = chipData.find(
                  (bet) => bet.type === getKeyByValue(marketType, type)
                );
                return {
                  type,
                  amount: parseNumberByPrecision(
                    1,
                    itemAmount[itemAmount.type],
                    userInfoStore.precisionForBetModel
                  ),
                };
              }),
            };
          }
          return item;
        }),
      };
    } else {
      allBets = {
        betModel: [
          this.createBetModelByCombination(chipData, betType, userInfoStore.selectedChip),
          ...(lastBetModel?.betModel || []),
        ],
      };
    }
    return allBets;
  }

  setDoBet(chipData, betType) {
    if (!betType) {
      const resultBetModel = this.handleChangeAllBetsModels(false, chipData);
      this.allBetsModels = [resultBetModel, ...this.allBetsModels];
    }
  }

  setResultNumberForTable(number) {
    clearTimeout(this.resultNumTimeout);
    this.resultNumberForTable = number;

    this.resultNumTimeeout = setTimeout(() => {
      if (this.resultNumberForTable) {
        this.resultNumberForTable = null;
      }
    }, 3000);
  }

  setDoubleBet({ combinations }) {
    soundService.play(RouletteSoundsName.chip);
    const resultBetModel = this.handleChangeAllBetsModels(combinations);
    this.setUndoBetCount(1);
    this.allBetsModels = [resultBetModel, ...this.allBetsModels];
  }

  setUndoBet() {
    if (this.allBetsModels.length) {
      soundService.play(RouletteSoundsName.undoClear);
      const newBetModel = [...this.allBetsModels];
      newBetModel.splice(0, this.undoBetCount[0]);
      this.undoBetCount.shift();
      this.allBetsModels = newBetModel;
    }
  }

  removeBetModel() {
    if (this.allBetsModels.length) {
      soundService.play(RouletteSoundsName.undoClear);
      this.allBetsModels = [];
    }
  }

  checkValidChip(nextChipModel, isDouble, betType) {
    const type = nextChipModel.chipType;

    const quickBetChip = userInfoStore.quickBets.find((item) => {
      return item.chip === nextChipModel[type];
    });
    const curChipValue = quickBetChip?.value;
    const prevCurrentChipsAllAmount =
      this.allBetsModels.length &&
      this.allBetsModels[0]?.betModel?.reduce(
        (acc, cur) => {
          if (
            nextChipModel?.betType &&
            getKeyByValue(marketType, cur.bet.type).includes(nextChipModel?.betType)
          ) {
            cur.includedBets.forEach((item) => {
              if (type === getKeyByValue(marketType, item.type)) {
                acc.currentChipTotal = sumWithDecimal(
                  userInfoStore.precisionForBetModel,
                  acc.currentChipTotal,
                  item.amount
                );
              }
            });
          }
          if (cur.includedBets) {
            cur.includedBets.forEach((item) => {
              if (type === getKeyByValue(marketType, item.type)) {
                acc.allChipTotal = sumWithDecimal(
                  userInfoStore.precisionForBetModel,
                  acc.allChipTotal,
                  item.amount
                );
              }
            });
          } else {
            if (type === getKeyByValue(marketType, cur.bet.type)) {
              acc.allChipTotal = sumWithDecimal(
                userInfoStore.precisionForBetModel,
                acc.allChipTotal,
                cur.bet.amount
              );
            }
            if (!nextChipModel?.betType && type === getKeyByValue(marketType, cur.bet.type)) {
              acc.currentChipTotal = sumWithDecimal(
                userInfoStore.precisionForBetModel,
                acc.currentChipTotal,
                cur.bet.amount
              );
            }
          }
          return acc;
        },
        {
          allChipTotal: 0,
          currentChipTotal: 0,
        }
      );

    // const maxLimitKey = Object.keys(additionalLimits).find((item) => {
    //   if (item.includes('/')) {
    //     const searchedValue = item.split('/');
    //     return searchedValue.some((item) => type.toLowerCase().match(item.toLowerCase()));
    //   }
    //   return type.toLowerCase().match(item.toLowerCase());
    // });
    // const maxLimit = additionalLimits[maxLimitKey].maxBet;
    // const minLimit = additionalLimits[maxLimitKey].minBet;

    if (isDouble) {
      // if (prevCurrentChipsAllAmount.allChipTotal * 2 > maxLimit) {
      //   return false;
      // } else {
      return {
        [type]: prevCurrentChipsAllAmount.currentChipTotal * 2,
        type,
        betType,
      };
      // }
    }

    // if (nextChipModel[type] < minLimit) {
    //   return;
    // }
    if (!prevCurrentChipsAllAmount.allChipTotal) {
      // if (nextChipModel[type] > maxLimit) {
      //   return false;
      // }
      return {
        [type]: curChipValue * (nextChipModel.chipValue || 1),
        type,
      };
    }
    // if (nextChipModel[type] + prevCurrentChipsAllAmount.allChipTotal > maxLimit) {
    //   return false;
    // }
    return {
      [type]: parseNumberByPrecision(
        1,
        sumWithDecimal(
          userInfoStore.precisionForBetModel,
          curChipValue * (nextChipModel.chipValue || 1),
          prevCurrentChipsAllAmount.currentChipTotal
        ),
        userInfoStore.precisionForBetModel
      ),
      type,
    };
  }

  handleDoBetWithValid(nextChipModel) {
    if (this.isBetsClosed) {
      return;
    }
    const { userBalance, limits, betAmount } = userInfoStore;
    const isValidResult = this.checkValidChip(nextChipModel);
    const total = allChipsTotal(this.allBetsModels[0], userInfoStore.precisionForBetModel) || 0;

    if (total + betAmount > userBalance || total > limits.maxBet) {
      // notificationStore.setNotificationType(betResponseCodeByName.NotEnoughBalance);
      // setTimeout(() => {
      //   notificationStore.setNotificationType(null);
      // }, 1000);
      // return;
    }
    if (!isValidResult) {
      // notificationStore.setNotificationType(betResponseCodeByName.InValidMaxBet);
      // setTimeout(() => {
      //   notificationStore.setNotificationType(null);
      // }, 1000);
      return;
    }
    this.setDoBet(isValidResult);
  }

  handleDoubleBetWithValid() {
    if (this.isBetsClosed) {
      return;
    }

    if (!this.allBetsModels[0]) return;

    const totalBetAmount = userInfoStore.displayCurrency
      ? userInfoStore.exchangedBetAmount
      : allChipsTotal(this.allBetsModels[0], userInfoStore.precisionForBetModel);
    if (totalBetAmount * 2 <= userInfoStore.limits.maxBet) {
      const isValidResult = this.allBetsModels[0]?.betModel?.reduce((acc, cur) => {
        if (cur.includedBets) {
          cur.includedBets.forEach((item) => {
            const chipType = getKeyByValue(marketType, item.type);
            const betType = getKeyByValue(marketType, cur.bet.type);
            acc.push({
              ...this.checkValidChip({ ...item, [chipType]: item.amount, chipType, betType }, true),
              betType,
            });
          });
        } else {
          const chipType = getKeyByValue(marketType, cur.bet.type);
          acc.push({
            ...this.checkValidChip({ ...cur.bet, [chipType]: cur.bet.amount, chipType }, true),
          });
        }
        return acc;
      }, []);

      if (isValidResult.some((item) => !item[item.type])) {
        // notificationStore.setNotificationType(betResponseCodeByName.InValidMaxBet);
        // setTimeout(() => {
        //   notificationStore.setNotificationType(null);
        // }, 1000);
        return;
      }

      const result = isValidResult.reduce(
        (acc, cur) => {
          if (cur) {
            const { type, betType } = cur;
            acc.combinations.push({
              [type]: cur[type],
              type,
              betType,
            });
          }
          return acc;
        },
        {
          combinations: [],
        }
      );

      const total = result.combinations.reduce((acc, cur) => {
        acc = sumWithDecimal(userInfoStore.precisionForBetModel, acc, cur[cur.type]);
        return acc;
      }, 0);

      if (total > userInfoStore.userBalance) {
        notificationStore.setNotification(ValidationType.NotEnoughMoney);
        return;
      }
      this.setDoubleBet(result);
    } else {
      notificationStore.setNotification(ValidationType.MaxBetLimit);
    }
  }

  resetStore() {
    this.isBetsClosed = false;
    this.result = {
      resultNumber: 0,
      isWin: false,
      odd: null,
      winAmount: null,
    };
    this.resultNumTimeout = null;
    this.undoBetCount = [];
    this.resultNumberForTable = null;
    this.allBetsModels = [];
    this.isSecondClick = false;
    this.lastResults = [];
  }
}

export const rouletteBetsStore = new RouletteBetsStore();
